import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useSubscription } from '../contexts/SubscriptionContext'
import { CreditCard, Check, X, Crown, Zap, Star, Shield, Users, Bell, BarChart3, Settings, ArrowRight, AlertCircle, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import subscriptionService from '../services/subscriptionService'
import organizationService from '../services/organizationService'
import stripeService from '../services/stripeService'

export default function Subscription() {
  const { currentUser, userProfile } = useAuth()
  const { subscription, usageStats, loading: subscriptionLoading, refreshSubscription } = useSubscription()
  const [upgradingPlan, setUpgradingPlan] = useState(null)
  const [billingPeriod, setBillingPeriod] = useState('monthly') // 'monthly' or 'yearly'
  const [orgSubscription, setOrgSubscription] = useState(null)
  const [orgUsageStats, setOrgUsageStats] = useState(null)
  const [orgLoading, setOrgLoading] = useState(false)

  // Load organization subscription data
  useEffect(() => {
    const loadOrgSubscription = async () => {
      if (!currentUser || !userProfile) return
      
      const orgId = organizationService.getPrimaryOrganizationId(userProfile, null)
      if (!orgId) return
      
      setOrgLoading(true)
      try {
        const orgSub = await subscriptionService.getOrganizationSubscription(orgId)
        const orgStats = await subscriptionService.getUsageStats(currentUser.uid, orgId)
        
        setOrgSubscription(orgSub)
        setOrgUsageStats(orgStats)
        
      } catch (error) {
        console.error('❌ Error loading organization subscription:', error)
      } finally {
        setOrgLoading(false)
      }
    }
    
    loadOrgSubscription()
  }, [currentUser, userProfile])

  // Use organization data if available and not free, otherwise fall back to user data
  // This ensures user trials are not overridden by free organization subscriptions
  const orgPlanKey = orgSubscription?.planType && String(orgSubscription.planType).toLowerCase()
  const displaySubscription = (orgSubscription && orgPlanKey && orgPlanKey !== 'free') ? orgSubscription : subscription
  const displayUsageStats = (orgSubscription && orgPlanKey && orgPlanKey !== 'free') ? orgUsageStats : usageStats
  const displayLoading = orgLoading || subscriptionLoading

  // Debug logging for subscription selection
  console.log('🔍 Subscription selection debug:', {
    userSubscription: subscription?.planType,
    orgSubscription: orgSubscription?.planType,
    displaySubscription: displaySubscription?.planType,
    orgSubscriptionExists: !!orgSubscription,
    orgIsFree: orgSubscription?.planType === 'free'
  })
  
  // Debug the display name generation
  if (displaySubscription) {
    console.log('🔍 Display subscription debug:', {
      planType: displaySubscription.planType,
      name: displaySubscription.name,
      trialDaysRemaining: displaySubscription.trialDaysRemaining,
      status: displaySubscription.status,
      accessLevel: displaySubscription.accessLevel,
      subscriptionType: displaySubscription.subscriptionType
    })
    
    const displayName = subscriptionService.getSubscriptionDisplayName(displaySubscription)
  }

  // Get effective limits (trial limits if active, fallback limits if expired)
  const effectiveLimits = subscriptionService.getEffectiveLimits(displaySubscription)


  const pricingTiers = [
    {
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: 'Basic features after trial',
      features: [
        '1 organization admin',
        '1 group',
        '3 alerts per month',
        'Basic push notifications',
        'No email alerts',
        'No pinned mapping',
        'No calendar posting',
        'No analytics'
      ],
      limits: {
        admins: 1,
        groups: 1,
        alerts: 3
      },
      popular: false,
      current: displaySubscription?.planType === 'free' && displaySubscription?.accessLevel === 'standard'
    },
    {
      name: 'Pro',
      monthlyPrice: 10,
      yearlyPrice: 100, // $10/month * 12 = $120, but showing $100 for yearly discount
      description: 'Great for small teams',
      features: [
        '2 organization admins',
        '1 group leader',
        '5 groups',
        '100 alerts per month',
        'No pinned mapping',
        'No email alerts',
        'No calendar posting',
        'No analytics',
        'Full web browser access',
        'Advanced push notifications',
        'Email support',
        'Mobile app access'
      ],
      limits: {
        admins: 2,
        groups: 5,
        alerts: 100
      },
      popular: true,
      current: displaySubscription?.planType === 'pro' || (displaySubscription?.accessLevel === 'pro' && displaySubscription?.status === 'trial')
    },
    {
      name: 'Premium',
      monthlyPrice: 25,
      yearlyPrice: 250, // $25/month * 12 = $300, but showing $250 for yearly discount
      description: 'Perfect for growing organizations',
      features: [
        '3 organization admins',
        '5 group leaders',
        '25 groups',
        '500 alerts per month',
        'Private groups',
        'Pinned mapping',
        'Calendar posting',
        'Analytics dashboard',
        'Email and push notifications',
        'Email support',
        'Full web browser access',
        'Mobile app access'
      ],
      limits: {
        admins: 3,
        groups: 25,
        alerts: 500
      },
      popular: false,
      current: displaySubscription?.planType === 'premium'
    },
    {
      name: 'Enterprise',
      monthlyPrice: null,
      yearlyPrice: null,
      customPrice: 'Custom Quote',
      description: 'For large organizations',
      features: [
        '3 organization admins',
        'Custom group leaders',
        'Custom groups',
        'Custom alerts',
        'Private groups',
        'Pinned mapping',
        'Calendar posting',
        'Analytics dashboard',
        'Email and push notifications',
        'Email support',
        'Full web browser access',
        'Mobile app access'
      ],
      limits: {
        admins: 3,
        groups: 999999,
        alerts: 999999
      },
      popular: false,
      current: displaySubscription?.planType === 'enterprise'
    }
  ]

  const handleUpgrade = async (planType) => {
    if (planType === 'free') return
    
    if (!currentUser || !userProfile) {
      toast.error('Please log in to upgrade your subscription')
      return
    }
    
    setUpgradingPlan(planType)
    try {
      console.log(`🛒 Starting upgrade to ${planType} plan (${billingPeriod})...`)
      
      await stripeService.createCheckoutSession(
        planType, 
        currentUser.uid, 
        userProfile.email,
        billingPeriod
      )
      
    } catch (error) {
      console.error('❌ Error upgrading subscription:', error)
      toast.error('Failed to start checkout process. Please try again.')
    } finally {
      setUpgradingPlan(null)
    }
  }

  const getUsagePercentage = (used, limit) => {
    if (limit === 999999) return 0 // Unlimited
    return Math.min((used / limit) * 100, 100)
  }

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 75) return 'text-yellow-600'
    return 'text-green-600'
  }

  const getProgressBarColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 75) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  if (displayLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the perfect plan for your organization. Upgrade or downgrade at any time.
          </p>
          <button 
            onClick={async () => {
              console.log('🔄 Manually refreshing subscription data...')
              await refreshSubscription()
            }} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Subscription Data
          </button>
        </div>

        {/* Current Subscription Status */}
        {displaySubscription && (
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <CreditCard className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Current Plan: {subscriptionService.getSubscriptionDisplayName(displaySubscription)}
                    </h3>
                    <p className="text-gray-600">
                      {displaySubscription.planType === 'enterprise' ? 'Custom Quote' : 
                       displaySubscription.planType === 'pro_trial' ? 
                         (displaySubscription.trialDaysRemaining > 0 
                           ? `${displaySubscription.trialDaysRemaining} day${displaySubscription.trialDaysRemaining === 1 ? '' : 's'} left in premium trial`
                           : 'Premium trial expired') :
                       displaySubscription.accessLevel === 'standard' ? 'Standard Plan' : 
                       displaySubscription.name || 'Free Plan'} - {displaySubscription.planType === 'enterprise' ? 'Custom Quote' : 
                       displaySubscription.planType === 'pro_trial' ? 'Free Trial' : 
                       displaySubscription.billingPeriod === 'year' ? `$${displaySubscription.yearlyPrice || 0}/year` :
                       displaySubscription.billingPeriod === 'month' ? `$${displaySubscription.monthlyPrice || 0}/month` :
                       `$${displaySubscription.price || 0}/month`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    displaySubscription.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : displaySubscription.status === 'trial'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {displaySubscription.status === 'active' ? 'Active' : 
                     displaySubscription.status === 'trial' ? 'Trial' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Trial Expiration Warning */}
            {displaySubscription.planType === 'pro_trial' && displaySubscription.trialDaysRemaining !== undefined && (
              <div className={`mt-4 p-4 rounded-lg border ${
                displaySubscription.trialDaysRemaining <= 0 
                  ? 'bg-red-50 border-red-200' 
                  : displaySubscription.trialDaysRemaining <= 3 
                  ? 'bg-yellow-50 border-yellow-200' 
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center">
                  <AlertCircle className={`h-5 w-5 mr-2 ${
                    displaySubscription.trialDaysRemaining <= 0 
                      ? 'text-red-500' 
                      : displaySubscription.trialDaysRemaining <= 3 
                      ? 'text-yellow-500' 
                      : 'text-blue-500'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      displaySubscription.trialDaysRemaining <= 0 
                        ? 'text-red-800' 
                        : displaySubscription.trialDaysRemaining <= 3 
                        ? 'text-yellow-800' 
                        : 'text-blue-800'
                    }`}>
                      {displaySubscription.trialDaysRemaining <= 0 
                        ? 'Your premium trial has expired. Upgrade to continue using premium features.'
                        : displaySubscription.trialDaysRemaining <= 3 
                        ? `Your premium trial expires in ${displaySubscription.trialDaysRemaining} day${displaySubscription.trialDaysRemaining === 1 ? '' : 's'}. Upgrade now to keep your premium features.`
                        : `Your premium trial has ${displaySubscription.trialDaysRemaining} day${displaySubscription.trialDaysRemaining === 1 ? '' : 's'} remaining.`}
                    </p>
                    {displaySubscription.trialDaysRemaining > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        After the trial ends, your account will revert to the Free plan with limited features.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Usage Statistics - Only show for non-standard users */}
        {displayUsageStats && displaySubscription && displaySubscription.accessLevel !== 'standard' && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Current Usage</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
                  <Bell className="h-5 w-5 text-blue-600" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Used this month</span>
                    <span className="font-medium">{displayUsageStats.usage?.alertsSent || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(displayUsageStats.usage?.alertsSent || 0, effectiveLimits?.maxAlertsPerMonth || 25))}`}
                      style={{ 
                        width: `${getUsagePercentage(displayUsageStats.usage?.alertsSent || 0, effectiveLimits?.maxAlertsPerMonth || 25)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {effectiveLimits?.maxAlertsPerMonth === 999999 ? '∞' : 
                     effectiveLimits?.maxAlertsPerMonth === 'custom' ? 'Custom' : 
                     `of ${effectiveLimits?.maxAlertsPerMonth || 25}`}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Groups</h3>
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Created</span>
                    <span className="font-medium">{displayUsageStats.usage?.groupsCreated || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(displayUsageStats.usage?.groupsCreated || 0, effectiveLimits?.maxGroups || 2))}`}
                      style={{ 
                        width: `${getUsagePercentage(displayUsageStats.usage?.groupsCreated || 0, effectiveLimits?.maxGroups || 2)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {effectiveLimits?.maxGroups === 999999 ? '∞' : 
                     effectiveLimits?.maxGroups === 'custom' ? 'Custom' : 
                     `of ${effectiveLimits?.maxGroups || 2}`}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Admins</h3>
                  <Shield className="h-5 w-5 text-purple-600" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Added</span>
                    <span className="font-medium">{displayUsageStats.usage?.adminsAdded || 0}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(displayUsageStats.usage?.adminsAdded || 0, effectiveLimits?.maxAdmins || 1))}`}
                      style={{ 
                        width: `${getUsagePercentage(displayUsageStats.usage?.adminsAdded || 0, effectiveLimits?.maxAdmins || 1)}%` 
                      }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {effectiveLimits?.maxAdmins === 999999 ? '∞' : 
                     effectiveLimits?.maxAdmins === 'custom' ? 'Custom' : 
                     `of ${effectiveLimits?.maxAdmins || 1}`}
                  </div>
                </div>
              </div>

              {/* Group Leaders box - only show for Pro and above */}
              {(displaySubscription?.planType === 'pro_trial' || displaySubscription?.planType === 'pro' || displaySubscription?.planType === 'premium' || displaySubscription?.planType === 'enterprise') && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Group Leaders</h3>
                    <UserCheck className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Added</span>
                      <span className="font-medium">{displayUsageStats.usage?.groupLeadersAdded || 0}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(displayUsageStats.usage?.groupLeadersAdded || 0, effectiveLimits?.maxGroupLeaders || 0))}`}
                        style={{ 
                          width: `${getUsagePercentage(displayUsageStats.usage?.groupLeadersAdded || 0, effectiveLimits?.maxGroupLeaders || 0)}%` 
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {effectiveLimits?.maxGroupLeaders === 999999 ? '∞' : 
                       effectiveLimits?.maxGroupLeaders === 'custom' ? 'Custom' : 
                       `of ${effectiveLimits?.maxGroupLeaders || 0}`}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}

        {/* Standard User Message */}
        {displaySubscription && displaySubscription.accessLevel === 'standard' && (
          <div className="mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Shield className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    Standard Access
                  </h3>
                  <p className="text-blue-800 mb-3">
                    You currently have standard access, which allows you to view alerts from organizations you follow and join groups as a member. 
                    You don't have admin privileges to create alerts, groups, or manage organizations.
                  </p>
                  <p className="text-blue-700 text-sm">
                    To get admin access, you'll need to be approved as an organization administrator or upgrade to a paid plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="mb-8">
          {/* Billing Period Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                  billingPeriod === 'yearly'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {pricingTiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative bg-white rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-lg ${
                tier.current
                  ? 'border-green-500 ring-2 ring-green-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              
              {tier.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Current Plan
                  </span>
                </div>
              )}

              <div className="p-6">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <p className="text-gray-600 mb-4">{tier.description}</p>
                  <div className="flex items-baseline justify-center">
                    {tier.customPrice ? (
                      <span className="text-2xl font-bold text-gray-900">{tier.customPrice}</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          ${billingPeriod === 'yearly' ? tier.yearlyPrice : tier.monthlyPrice}
                        </span>
                        <span className="text-gray-600 ml-1">
                          /{billingPeriod === 'yearly' ? 'year' : 'month'}
                        </span>
                        {billingPeriod === 'yearly' && tier.yearlyPrice > 0 && (
                          <div className="text-sm text-gray-500 mt-1">
                            ${Math.round(tier.yearlyPrice / 12)}/month
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className={`h-12 ${tier.name === 'Free' ? 'mt-8' : tier.name === 'Premium' ? 'mt-16' : ''}`}>
                  <button
                    onClick={() => handleUpgrade(tier.name.toLowerCase())}
                    disabled={tier.current || upgradingPlan}
                    className={`w-full h-12 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      tier.current
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {tier.current ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Current Plan
                      </>
                    ) : upgradingPlan === tier.name.toLowerCase() ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        {tier.name === 'Free' ? 'Get Started' : tier.name === 'Enterprise' ? 'Contact Sales' : 'Upgrade'}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Need Help Choosing?
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Our team is here to help you find the perfect plan for your organization. 
              Contact us for personalized recommendations and custom enterprise solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Contact Sales
              </button>
              <button className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                View Documentation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}